import { cur } from '@/lib/currency';
import { useEffect, useState } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, ShoppingBag, FileText, CreditCard, Gift, LogOut, Store, 
  Calendar, DollarSign, TrendingUp, ChevronRight, RefreshCw, Download,
  Clock, CheckCircle, AlertCircle, Loader2, Ruler, Cpu, Star
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { localToday } from '@/lib/dateRange';

function CustomerPortalLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    const customerData = localStorage.getItem('customer_data');
    
    if (!token) {
      navigate('/customer-portal');
      return;
    }
    
    if (customerData) {
      setCustomer(JSON.parse(customerData));
    }
  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('customer_token');
    try {
      await api.post(`/customer-portal/logout?token=${token}`);
    } catch {}
    localStorage.removeItem('customer_token');
    localStorage.removeItem('customer_data');
    navigate('/customer-portal');
    toast.success('Logged out successfully');
  };

  const navItems = [
    { path: '/customer-portal/dashboard', icon: User, label: 'Overview' },
    { path: '/customer-portal/orders', icon: ShoppingBag, label: 'Orders' },
    { path: '/customer-portal/statements', icon: FileText, label: 'Statements' },
    { path: '/customer-portal/invoices', icon: CreditCard, label: 'Invoices' },
    { path: '/customer-portal/appointments', icon: Calendar, label: 'Appointments' },
    { path: '/customer-portal/loyalty', icon: Gift, label: 'Loyalty' },
  ];

  return (
    <div className="min-h-screen bg-muted/50 dark:bg-foreground">
      {/* Header */}
      <header className="bg-white dark:bg-muted border-b shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Store className="text-white" size={20} />
            </div>
            <div>
              <h1 className="font-bold text-foreground dark:text-white">Customer Portal</h1>
              <p className="text-xs text-muted-foreground">{customer?.name || 'Loading...'}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="customer-logout-btn">
            <LogOut size={16} className="mr-1" /> Logout
          </Button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white dark:bg-muted border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex overflow-x-auto gap-1 py-2">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive 
                      ? 'bg-success/20 text-success dark:bg-emerald-900 dark:text-emerald-300' 
                      : 'text-muted-foreground hover:bg-muted dark:text-muted-foreground dark:hover:bg-stone-700'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={16} />
                  {item.label}
                </Link>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
}

export function CustomerPortalDashboard() {
  const [profile, setProfile] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    try {
      const [profileRes, ordersRes] = await Promise.all([
        api.get(`/customer-portal/profile?token=${token}`),
        api.get(`/customer-portal/orders?token=${token}&limit=5`),
      ]);
      setProfile(profileRes.data);
      setRecentOrders(ordersRes.data.orders || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-success" size={32} />
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        {/* Welcome */}
        <div>
          <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="customer-welcome">
            Welcome, {profile?.name}!
          </h2>
          <p className="text-muted-foreground">Here's your account summary</p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-success/30 bg-gradient-to-br from-emerald-50 to-emerald-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-success">Credit Balance</p>
                  <p className="text-xl font-bold text-success" data-testid="credit-balance">
                    {cur()} {(profile?.credit_balance || 0).toLocaleString()}
                  </p>
                </div>
                <DollarSign className="text-success" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-purple-600">Loyalty Points</p>
                  <p className="text-xl font-bold text-purple-700" data-testid="loyalty-points">
                    {(profile?.loyalty_points || 0).toLocaleString()}
                  </p>
                </div>
                <Gift className="text-purple-500" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-warning/30 bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-warning">Loyalty Tier</p>
                  <p className="text-xl font-bold text-warning" data-testid="loyalty-tier">
                    {profile?.loyalty_tier || 'Bronze'}
                  </p>
                </div>
                <TrendingUp className="text-warning" size={24} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-info/30 bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-info">Member Since</p>
                  <p className="text-lg font-bold text-info">
                    {profile?.member_since ? format(new Date(profile.member_since), 'MMM yyyy') : '-'}
                  </p>
                </div>
                <Calendar className="text-info" size={24} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-lg">Recent Orders</CardTitle>
            <Link to="/customer-portal/orders">
              <Button variant="ghost" size="sm">
                View All <ChevronRight size={14} />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            {recentOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <ShoppingBag className="mx-auto mb-2 text-muted-foreground" size={32} />
                No orders yet
              </div>
            ) : (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-muted/50 dark:bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-success/20 rounded-lg flex items-center justify-center">
                        <ShoppingBag className="text-success" size={18} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{order.description || 'Order'}</p>
                        <p className="text-xs text-muted-foreground">
                          {order.date ? format(new Date(order.date), 'dd MMM yyyy') : '-'} • {order.branch_name}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-success">{cur()} {order.total?.toLocaleString() || 0}</p>
                      <Badge variant="outline" className="text-xs">
                        {order.payment_mode}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}

export function CustomerPortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchOrders();
  }, [page]);

  const fetchOrders = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    setLoading(true);
    try {
      const res = await api.get(`/customer-portal/orders?token=${token}&page=${page}&limit=20`);
      setOrders(res.data.orders || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="orders-title">
          Order History
        </h2>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin text-success" size={32} />
          </div>
        ) : orders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingBag className="mx-auto mb-3 text-muted-foreground" size={48} />
              <p className="text-muted-foreground">No orders found</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="space-y-3">
              {orders.map((order) => (
                <Card key={order.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-success/20 rounded-xl flex items-center justify-center">
                          <ShoppingBag className="text-success" size={24} />
                        </div>
                        <div>
                          <p className="font-medium">{order.description || `Order #${order.id.slice(-6)}`}</p>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar size={12} />
                            {order.date ? format(new Date(order.date), 'dd MMM yyyy, h:mm a') : '-'}
                            <span>•</span>
                            {order.branch_name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-success">{cur()} {order.total?.toLocaleString()}</p>
                        <Badge variant={order.payment_mode === 'credit' ? 'secondary' : 'default'}>
                          {order.payment_mode}
                        </Badge>
                      </div>
                    </div>
                    {order.items && order.items.length > 0 && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Items:</p>
                        <div className="flex flex-wrap gap-2">
                          {order.items.map((item, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {item.name || item.item_name} x{item.quantity || 1}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className="flex items-center px-4 text-sm text-muted-foreground">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </CustomerPortalLayout>
  );
}

export function CustomerPortalStatements() {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    fetchStatement();
  }, []);

  const fetchStatement = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    setLoading(true);
    try {
      let url = `/customer-portal/statements?token=${token}`;
      if (dateRange.start) url += `&start_date=${dateRange.start}`;
      if (dateRange.end) url += `&end_date=${dateRange.end}`;
      
      const res = await api.get(url);
      setStatement(res.data);
    } catch (error) {
      toast.error('Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="statements-title">
            Account Statement
          </h2>
          <div className="flex gap-2 items-center">
            <input
              type="date"
              className="px-3 py-2 border rounded-lg text-sm"
              value={dateRange.start}
              onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            />
            <span className="text-muted-foreground">to</span>
            <input
              type="date"
              className="px-3 py-2 border rounded-lg text-sm"
              value={dateRange.end}
              onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            />
            <Button size="sm" onClick={fetchStatement}>
              <RefreshCw size={14} className="mr-1" /> Filter
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin text-success" size={32} />
          </div>
        ) : (
          <>
            {/* Balance Summary */}
            <Card className="border-success/30 bg-gradient-to-r from-emerald-50 to-teal-50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-success">Current Balance</p>
                    <p className="text-3xl font-bold text-success" data-testid="current-balance">
                      {cur()} {(statement?.current_balance || 0).toLocaleString()}
                    </p>
                  </div>
                  <DollarSign className="text-emerald-300" size={48} />
                </div>
              </CardContent>
            </Card>

            {/* Transactions Table */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {!statement?.transactions || statement.transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <FileText className="mx-auto mb-2 text-muted-foreground" size={32} />
                    No transactions found
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Description</th>
                          <th className="px-3 py-2 text-right">Debit</th>
                          <th className="px-3 py-2 text-right">Credit</th>
                          <th className="px-3 py-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.transactions.map((t, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="px-3 py-2 text-muted-foreground">
                              {t.date ? format(new Date(t.date), 'dd/MM/yyyy') : '-'}
                            </td>
                            <td className="px-3 py-2">{t.description}</td>
                            <td className="px-3 py-2 text-right text-error">
                              {t.debit > 0 ? `${cur()} ${t.debit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right text-success">
                              {t.credit > 0 ? `${cur()} ${t.credit.toLocaleString()}` : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              {cur()} {(t.balance || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </CustomerPortalLayout>
  );
}

export function CustomerPortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    fetchInvoices();
    // Check for payment success/cancel from URL
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      toast.success('Payment completed successfully!');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (params.get('payment') === 'cancelled') {
      toast.info('Payment was cancelled');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const fetchInvoices = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    try {
      const res = await api.get(`/customer-portal/invoices?token=${token}`);
      setInvoices(res.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const handlePayInvoice = async (invoiceId) => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    setPayingId(invoiceId);
    try {
      const res = await api.post(`/customer-portal/invoices/${invoiceId}/pay?token=${token}`, {
        origin_url: window.location.origin,
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate payment');
    } finally {
      setPayingId(null);
    }
  };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="invoices-title">
          Invoices
        </h2>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="animate-spin text-success" size={32} />
          </div>
        ) : invoices.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CreditCard className="mx-auto mb-3 text-muted-foreground" size={48} />
              <p className="text-muted-foreground">No invoices found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {invoices.map((invoice) => (
              <Card key={invoice.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-info/20 rounded-xl flex items-center justify-center">
                        <FileText className="text-info" size={24} />
                      </div>
                      <div>
                        <p className="font-medium">Invoice #{invoice.invoice_number || invoice.id.slice(-6)}</p>
                        <p className="text-sm text-muted-foreground">
                          {invoice.date ? format(new Date(invoice.date), 'dd MMM yyyy') : '-'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      <p className="text-xl font-bold">{cur()} {invoice.total?.toLocaleString()}</p>
                      <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'} data-testid="invoice-status-badge">
                        {invoice.status || 'issued'}
                      </Badge>
                      {invoice.status !== 'paid' && (
                        <div>
                          <Button size="sm" className="rounded-full text-xs bg-success hover:bg-emerald-700 mt-1" onClick={() => handlePayInvoice(invoice.id)} disabled={payingId === invoice.id} data-testid="pay-invoice-btn">
                            {payingId === invoice.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <CreditCard size={12} className="mr-1" />}
                            Pay Now
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  {invoice.vat > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Includes VAT: {cur()} {invoice.vat.toLocaleString()}
                    </p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </CustomerPortalLayout>
  );
}

export function CustomerPortalLoyalty() {
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyalty();
  }, []);

  const fetchLoyalty = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;

    try {
      const res = await api.get(`/customer-portal/loyalty?token=${token}`);
      setLoyalty(res.data);
    } catch (error) {
      toast.error('Failed to load loyalty data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <CustomerPortalLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-success" size={32} />
        </div>
      </CustomerPortalLayout>
    );
  }

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="loyalty-title">
          Loyalty Program
        </h2>

        {/* Points & Tier */}
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-purple-600">Your Points</p>
                  <p className="text-4xl font-bold text-purple-700" data-testid="loyalty-points-value">
                    {(loyalty?.current_points || 0).toLocaleString()}
                  </p>
                </div>
                <Gift className="text-purple-300" size={48} />
              </div>
            </CardContent>
          </Card>
          <Card className="border-warning/30 bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-warning">Current Tier</p>
                  <p className="text-4xl font-bold text-warning" data-testid="loyalty-tier-value">
                    {loyalty?.current_tier || 'Bronze'}
                  </p>
                  {loyalty?.next_tier && (
                    <p className="text-xs text-warning mt-1">
                      {loyalty.points_to_next_tier} pts to {loyalty.next_tier}
                    </p>
                  )}
                </div>
                <TrendingUp className="text-amber-300" size={48} />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tier Benefits */}
        {loyalty?.tier_benefits && loyalty.tier_benefits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Benefits</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {loyalty.tier_benefits.map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                    <CheckCircle className="text-success" size={16} />
                    <span className="text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Points History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Points History</CardTitle>
          </CardHeader>
          <CardContent>
            {!loyalty?.history || loyalty.history.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Clock className="mx-auto mb-2 text-muted-foreground" size={32} />
                No points history yet
              </div>
            ) : (
              <div className="space-y-3">
                {loyalty.history.map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        item.type === 'earn' ? 'bg-success/20' : 'bg-error/20'
                      }`}>
                        {item.type === 'earn' ? (
                          <TrendingUp className="text-success" size={14} />
                        ) : (
                          <Gift className="text-error" size={14} />
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{item.description || (item.type === 'earn' ? 'Points Earned' : 'Points Redeemed')}</p>
                        <p className="text-xs text-muted-foreground">
                          {item.date ? format(new Date(item.date), 'dd MMM yyyy') : '-'}
                        </p>
                      </div>
                    </div>
                    <span className={`font-bold ${item.type === 'earn' ? 'text-success' : 'text-error'}`}>
                      {item.type === 'earn' ? '+' : '-'}{item.points}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </CustomerPortalLayout>
  );
}

export default CustomerPortalLayout;

// ── Tailoring Order Tracking ─────────────────────────────────────
export function CustomerPortalTailoring() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('customer_token');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/customer-portal/tailoring-orders?token=${token}`);
        setOrders(res.data);
      } catch { /* empty */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const STATUS_COLORS = { received: 'bg-muted text-foreground', cutting: 'bg-info/20 text-info', stitching: 'bg-warning/20 text-warning', finishing: 'bg-purple-100 text-purple-700', ready: 'bg-success/20 text-success', delivered: 'bg-success/20 text-success' };
  const STEPS = ['received', 'cutting', 'stitching', 'finishing', 'ready', 'delivered'];

  if (loading) return <CustomerPortalLayout><div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" /></div></CustomerPortalLayout>;

  return (
    <CustomerPortalLayout>
      <div className="space-y-4" data-testid="portal-tailoring">
        <h2 className="text-lg font-semibold font-outfit flex items-center gap-2"><Ruler size={18} />Tailoring Orders</h2>
        {orders.length === 0 ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No tailoring orders found</CardContent></Card> : (
          <div className="space-y-3">
            {orders.map(o => {
              const stepIdx = STEPS.indexOf(o.status || 'received');
              return (
                <Card key={o.id} data-testid={`order-${o.id}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium text-sm">{o.garment_type}</span>
                        <Badge className={`ml-2 text-[10px] border-0 capitalize ${STATUS_COLORS[o.status] || ''}`}>{o.status}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">Due: {o.delivery_date || 'TBD'}</span>
                    </div>
                    {/* Progress Steps */}
                    <div className="flex items-center gap-1 mt-3">
                      {STEPS.map((step, i) => (
                        <div key={step} className="flex items-center flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${i <= stepIdx ? 'bg-success text-white' : 'bg-muted text-muted-foreground'}`}>{i <= stepIdx ? '✓' : i + 1}</div>
                          {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < stepIdx ? 'bg-success' : 'bg-muted'}`} />}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {STEPS.map(s => <span key={s} className="text-[8px] text-muted-foreground capitalize flex-1 text-center">{s}</span>)}
                    </div>
                    {o.balance && <div className="mt-2 text-xs text-muted-foreground">Balance Due: <span className="font-semibold text-error">{o.balance}</span></div>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </CustomerPortalLayout>
  );
}


// ── Repair Order Tracking ────────────────────────────────────────
export function CustomerPortalRepairs() {
  const [repairs, setRepairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem('customer_token');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/customer-portal/repair-orders?token=${token}`);
        setRepairs(res.data);
      } catch { /* empty */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const STATUS_COLORS = { received: 'bg-muted text-foreground', diagnosing: 'bg-info/20 text-info', repairing: 'bg-warning/20 text-warning', ready: 'bg-success/20 text-success', delivered: 'bg-success/20 text-success' };
  const STEPS = ['received', 'diagnosing', 'repairing', 'ready', 'delivered'];

  if (loading) return <CustomerPortalLayout><div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" /></div></CustomerPortalLayout>;

  return (
    <CustomerPortalLayout>
      <div className="space-y-4" data-testid="portal-repairs">
        <h2 className="text-lg font-semibold font-outfit flex items-center gap-2"><Cpu size={18} />Repair Orders</h2>
        {repairs.length === 0 ? <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No repair orders found</CardContent></Card> : (
          <div className="space-y-3">
            {repairs.map(r => {
              const stepIdx = STEPS.indexOf(r.status || 'received');
              return (
                <Card key={r.id} data-testid={`repair-${r.id}`}>
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-medium text-sm">{r.device_name || r.product_name}</span>
                        <Badge className={`ml-2 text-[10px] border-0 capitalize ${STATUS_COLORS[r.status] || ''}`}>{r.status}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{r.brand} {r.model}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-3">
                      {STEPS.map((step, i) => (
                        <div key={step} className="flex items-center flex-1">
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${i <= stepIdx ? 'bg-info text-white' : 'bg-muted text-muted-foreground'}`}>{i <= stepIdx ? '✓' : i + 1}</div>
                          {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 ${i < stepIdx ? 'bg-info' : 'bg-muted'}`} />}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-1">
                      {STEPS.map(s => <span key={s} className="text-[8px] text-muted-foreground capitalize flex-1 text-center">{s}</span>)}
                    </div>
                    {r.estimated_cost > 0 && <div className="mt-2 text-xs text-muted-foreground">Estimated: <span className="font-semibold">{r.estimated_cost}</span></div>}
                    {r.issue_description && <div className="mt-1 text-xs text-muted-foreground">Issue: {r.issue_description}</div>}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </CustomerPortalLayout>
  );
}


// ── Customer Feedback ────────────────────────────────────────────
export function CustomerPortalFeedback() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ rating: 5, category: 'general', comment: '' });
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem('customer_token');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/customer-portal/feedback?token=${token}`);
        setFeedback(res.data);
      } catch { /* empty */ }
      finally { setLoading(false); }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/customer-portal/feedback?token=${token}`, form);
      setForm({ rating: 5, category: 'general', comment: '' });
      const res = await api.get(`/customer-portal/feedback?token=${token}`);
      setFeedback(res.data);
      toast.success('Feedback submitted');
    } catch { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <CustomerPortalLayout><div className="flex items-center justify-center h-32"><Loader2 className="animate-spin" /></div></CustomerPortalLayout>;

  return (
    <CustomerPortalLayout>
      <div className="space-y-4" data-testid="portal-feedback">
        <h2 className="text-lg font-semibold font-outfit flex items-center gap-2"><Star size={18} />Feedback</h2>

        <Card>
          <CardHeader><CardTitle className="text-sm">Share Your Experience</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label className="text-xs">Rating</Label>
                <div className="flex gap-1 mt-1">
                  {[1, 2, 3, 4, 5].map(r => (
                    <button key={r} type="button" onClick={() => setForm({ ...form, rating: r })} className={`w-8 h-8 rounded-lg text-sm font-bold ${form.rating >= r ? 'bg-amber-400 text-white' : 'bg-muted text-muted-foreground'}`} data-testid={`rating-${r}`}>{r}</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                  <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="product">Product Quality</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Comment</Label>
                <textarea value={form.comment} onChange={e => setForm({ ...form, comment: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-20 resize-none" placeholder="Tell us about your experience..." data-testid="feedback-comment" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-feedback">{submitting ? <Loader2 size={14} className="animate-spin" /> : 'Submit Feedback'}</Button>
            </form>
          </CardContent>
        </Card>

        {feedback.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-sm">Your Previous Feedback</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {feedback.map(f => (
                <div key={f.id} className="p-3 rounded-lg bg-muted/50 border" data-testid={`feedback-${f.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">{[1, 2, 3, 4, 5].map(r => <span key={r} className={r <= f.rating ? 'text-amber-400' : 'text-muted-foreground'}>&#9733;</span>)}</div>
                    <Badge variant="outline" className="text-[10px] capitalize">{f.category}</Badge>
                    <span className="text-[10px] text-muted-foreground">{new Date(f.created_at).toLocaleDateString()}</span>
                  </div>
                  {f.comment && <p className="text-xs">{f.comment}</p>}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </CustomerPortalLayout>
  );
}


export function CustomerPortalAppointments() {
  const [appointments, setAppointments] = useState([]);
  const [services, setServices] = useState({ services: [], stylists: [], branches: [] });
  const [loading, setLoading] = useState(true);
  const [showBook, setShowBook] = useState(false);
  const [booking, setBooking] = useState(false);
  const [form, setForm] = useState({ branch_id: '', service_id: '', service_name: '', stylist_id: '', stylist_name: '', date: '', time: '', duration_minutes: 30, price: 0, notes: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const [apptRes, svcRes] = await Promise.all([
        api.get(`/customer-portal/appointments?token=${token}`),
        api.get(`/customer-portal/services?token=${token}`),
      ]);
      setAppointments(apptRes.data.appointments || []);
      setServices(svcRes.data);
    } catch { toast.error('Failed to load appointments'); }
    finally { setLoading(false); }
  };

  const handleBook = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('customer_token');
    if (!token || !form.date || !form.time) { toast.error('Please fill date and time'); return; }
    setBooking(true);
    try {
      await api.post(`/customer-portal/appointments?token=${token}`, form);
      toast.success('Appointment booked!');
      setShowBook(false);
      setForm({ branch_id: '', service_id: '', service_name: '', stylist_id: '', stylist_name: '', date: '', time: '', duration_minutes: 30, price: 0, notes: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to book'); }
    finally { setBooking(false); }
  };

  const handleCancel = async (id) => {
    const token = localStorage.getItem('customer_token');
    if (!window.confirm('Cancel this appointment?')) return;
    try {
      await api.delete(`/customer-portal/appointments/${id}?token=${token}`);
      toast.success('Appointment cancelled');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel'); }
  };

  const handleServiceChange = (serviceId) => {
    const svc = services.services.find(s => s.id === serviceId);
    setForm(prev => ({
      ...prev,
      service_id: serviceId,
      service_name: svc?.name || '',
      price: svc?.price || 0,
      duration_minutes: svc?.duration || 30,
    }));
  };

  const handleStylistChange = (stylistId) => {
    const st = services.stylists.find(s => s.id === stylistId);
    setForm(prev => ({ ...prev, stylist_id: stylistId, stylist_name: st?.name || '' }));
  };

  const statusColor = { booked: 'bg-info/20 text-info', in_progress: 'bg-warning/20 text-warning', completed: 'bg-success/20 text-success', cancelled: 'bg-error/20 text-error' };

  return (
    <CustomerPortalLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-foreground dark:text-white" data-testid="appointments-title">Appointments</h2>
          <Button className="rounded-full bg-success hover:bg-emerald-700" onClick={() => setShowBook(!showBook)} data-testid="book-appointment-btn">
            <Calendar size={16} className="mr-1" />{showBook ? 'Close' : 'Book Appointment'}
          </Button>
        </div>

        {showBook && (
          <Card className="border-success/30">
            <CardHeader><CardTitle className="text-sm">Book New Appointment</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleBook} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {services.branches.length > 0 && (
                    <div>
                      <Label className="text-xs">Branch</Label>
                      <Select value={form.branch_id} onValueChange={v => setForm({ ...form, branch_id: v })}>
                        <SelectTrigger className="h-9" data-testid="appt-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                        <SelectContent>{services.branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Service</Label>
                    <Select value={form.service_id} onValueChange={handleServiceChange}>
                      <SelectTrigger className="h-9" data-testid="appt-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                      <SelectContent>{services.services.map(s => <SelectItem key={s.id} value={s.id}>{s.name} - {cur()} {s.price}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {services.stylists.length > 0 && (
                    <div>
                      <Label className="text-xs">Stylist / Staff</Label>
                      <Select value={form.stylist_id} onValueChange={handleStylistChange}>
                        <SelectTrigger className="h-9" data-testid="appt-stylist-select"><SelectValue placeholder="Select stylist" /></SelectTrigger>
                        <SelectContent>{services.stylists.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Date *</Label>
                    <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-9" required data-testid="appt-date-input" min={localToday()} />
                  </div>
                  <div>
                    <Label className="text-xs">Time *</Label>
                    <input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-9" required data-testid="appt-time-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Notes</Label>
                    <input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full border rounded-lg p-2 text-sm h-9" placeholder="Any special requests..." data-testid="appt-notes-input" />
                  </div>
                </div>
                {form.service_name && (
                  <div className="p-2 bg-success/10 rounded-lg text-sm">
                    <span className="font-medium">{form.service_name}</span> - {form.duration_minutes} min - <span className="font-bold text-success">{cur()} {form.price}</span>
                  </div>
                )}
                <Button type="submit" disabled={booking} className="w-full bg-success hover:bg-emerald-700" data-testid="confirm-booking-btn">
                  {booking ? <Loader2 size={14} className="animate-spin mr-1" /> : <CheckCircle size={14} className="mr-1" />}
                  Confirm Booking
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-success" size={32} /></div>
        ) : appointments.length === 0 ? (
          <Card><CardContent className="py-12 text-center"><Calendar className="mx-auto mb-3 text-muted-foreground" size={48} /><p className="text-muted-foreground">No appointments yet</p></CardContent></Card>
        ) : (
          <div className="grid gap-4">
            {appointments.map(appt => (
              <Card key={appt.id} className="hover:shadow-md transition-shadow" data-testid={`appointment-${appt.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-success/20 rounded-xl flex items-center justify-center">
                        <Calendar className="text-success" size={24} />
                      </div>
                      <div>
                        <p className="font-medium">{appt.service_name || 'Appointment'}</p>
                        <p className="text-sm text-muted-foreground">{appt.date} at {appt.time}</p>
                        {appt.stylist_name && <p className="text-xs text-muted-foreground">with {appt.stylist_name}</p>}
                      </div>
                    </div>
                    <div className="text-right space-y-1">
                      {appt.price > 0 && <p className="font-bold">{cur()} {appt.price}</p>}
                      <Badge className={statusColor[appt.status] || 'bg-muted text-foreground'} data-testid="appt-status-badge">{appt.status}</Badge>
                      {appt.status === 'booked' && (
                        <div><Button size="sm" variant="outline" className="text-xs text-error border-error/30 rounded-full mt-1" onClick={() => handleCancel(appt.id)} data-testid="cancel-appt-btn">Cancel</Button></div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </CustomerPortalLayout>
  );
}
